'use client'

import { useCallback, type ReactNode } from 'react'
import { LocalAudioTrack } from 'livekit-client'

import { useLocalParticipant } from '@/src/hooks/participant/useLocalParticipant'
import { useNoiseFilterMode } from '@/src/hooks/room/track/useNoiseFilterMode'
import { useNoiseFilterProcessor } from '@/src/hooks/room/track/useNoiseFilterProcessor'
import { useAppSelector } from '@/src/store/hooks'
import { selectRoomState } from '@/src/store/slices/room'
import { selectAccessToken } from '@/src/store/slices/token'

interface NoiseFilterProviderProps {
  children: ReactNode
}

/**
 * In-call оболочка: вешает серверный шумодав на локальный микрофон.
 *
 * UI выбора живёт в MediaDeviceMenu через свои хуки. Здесь только lifecycle
 * процессора и тихий откат в `off`, если сервис на ноде не ответил.
 */
export function NoiseFilterProvider({ children }: NoiseFilterProviderProps) {
  const { livekit_url } = useAppSelector(selectRoomState)
  const accessToken = useAppSelector(selectAccessToken)
  const { microphoneTrack } = useLocalParticipant()
  const { mode, setMode } = useNoiseFilterMode()

  const track =
    microphoneTrack?.track instanceof LocalAudioTrack ? microphoneTrack.track : undefined

  const handleFallbackToNone = useCallback(() => {
    if (mode !== 'off') {
      setMode('off')
    }
  }, [mode, setMode])

  useNoiseFilterProcessor({
    mode,
    track,
    livekitUrl: livekit_url,
    accessToken,
    enabled: true,
    onFallbackToNone: handleFallbackToNone,
  })

  return <>{children}</>
}
